import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { lazy, Suspense } from 'react';
import { chartSx } from './chart-style';

// Loaded on demand so the charts package stays out of the app shell bundle.
const LineChart = lazy(() =>
  import('@mui/x-charts/LineChart').then((m) => ({ default: m.LineChart })),
);

export interface TrendChartSeries {
  id: string;
  label: string;
  data: (number | null)[];
  color: string;
}

interface TrendChartProps {
  labels: string[];
  /** Longer heading per point for the tooltip, e.g. the week's date range (defaults to `labels`). */
  tooltipLabels?: (string | undefined)[];
  series: TrendChartSeries[];
  valueFormatter: (value: number | null) => string;
  /** Shorter labels for the y axis (defaults to `valueFormatter`). */
  axisFormatter?: (value: number) => string;
  height?: number;
  /** Filled area under a single series (one-location view). */
  area?: boolean;
  /** `symlog` keeps small values readable next to very large ones; it handles 0, unlike `log`. */
  scale?: 'linear' | 'symlog';
}

/**
 * Y ticks for the symlog scale: 0 plus powers of ten up to the first one at or above `max`.
 * The scale's own ticks are linear (0, 2k, 4k, …), which bunch up at the top.
 */
export function symlogTicks(max: number): number[] {
  if (!(max > 0)) return [0];
  const ticks = [0];
  for (let tick = 1; ; tick *= 10) {
    ticks.push(tick);
    if (tick >= max) return ticks;
  }
}

/** Line chart for 13-week / 12-month trends, themed like the prototype. */
export function TrendChart({
  labels,
  tooltipLabels,
  series,
  valueFormatter,
  axisFormatter,
  height = 300,
  area,
  scale = 'linear',
}: TrendChartProps) {
  const yValueFormatter = (v: number | null) =>
    v === null ? '' : axisFormatter ? axisFormatter(v) : valueFormatter(v);
  const ticks =
    scale === 'symlog'
      ? symlogTicks(
          Math.max(0, ...series.flatMap((s) => s.data.filter((v): v is number => v !== null))),
        )
      : undefined;
  return (
    <Box sx={[{ width: '100%' }, chartSx]}>
      <Suspense fallback={<Skeleton variant="rounded" height={height} />}>
        <LineChart
          height={height}
          // Taller than the 25px default: x-charts empties tick labels whose measured height
          // (Manrope at 12px) does not fit the axis.
          xAxis={[
            {
              scaleType: 'point',
              data: labels,
              height: 34,
              valueFormatter: (label: string, context) =>
                context.location === 'tooltip'
                  ? (tooltipLabels?.[labels.indexOf(label)] ?? label)
                  : label,
            },
          ]}
          yAxis={[
            ticks
              ? {
                  scaleType: 'symlog',
                  min: 0,
                  max: ticks[ticks.length - 1],
                  tickInterval: ticks,
                  valueFormatter: yValueFormatter,
                  width: 64,
                }
              : { valueFormatter: yValueFormatter, width: 64 },
          ]}
          series={series.map((s) => ({
            id: s.id,
            label: s.label,
            data: s.data,
            color: s.color,
            curve: 'monotoneX',
            showMark: series.length === 1,
            area: area && series.length === 1,
            valueFormatter: (v: number | null) => valueFormatter(v),
          }))}
          // Hover list runs from the largest value to the smallest.
          slotProps={{ tooltip: { trigger: 'axis', sort: 'desc' } }}
          grid={{ horizontal: true }}
          hideLegend={series.length < 2}
          margin={{ top: 16, right: 26, bottom: 8, left: 8 }}
        />
      </Suspense>
    </Box>
  );
}
